import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getUserDisplayName } from './userMapping';

/**
 * Slack으로 재고 변경 알림 발송
 * @param {string} eventType - 이벤트 타입 (inventoryAdd, inventoryUpdate, inventoryDelete, inventoryMove, quantityChange, sale)
 * @param {object} data - 알림 데이터
 * @param {string} userEmail - 변경한 사용자 이메일
 */
export const sendSlackNotification = async (eventType, data, userEmail) => {
  try {
    // Webhook 목록 가져오기
    const webhooksRef = collection(db, 'settings_webhooks');
    const snapshot = await getDocs(webhooksRef);

    const webhooks = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(webhook => webhook.active && webhook.events?.[eventType]);

    if (webhooks.length === 0) {
      return; // 활성화된 webhook이 없으면 종료
    }

    // Slack 메시지 포맷 생성
    const message = formatSlackMessage(eventType, data, userEmail);

    // Netlify Function 프록시를 통해 발송 (CORS 우회)
    const proxyUrl = process.env.NODE_ENV === 'production'
      ? '/.netlify/functions/slack-proxy'
      : 'https://inventory.oomseoul.com/.netlify/functions/slack-proxy'; // 로컬 개발 시 배포된 프록시 사용

    const promises = webhooks.map(webhook =>
      fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          webhookUrl: webhook.url,
          message: message
        })
      })
    );

    await Promise.all(promises);
    console.log('✅ Slack 알림 발송 완료:', eventType);
  } catch (error) {
    console.error('❌ Slack 알림 발송 오류:', error);
    // 알림 발송 실패해도 원래 작업은 계속 진행
  }
};

/**
 * Slack 메시지 포맷 생성
 */
const formatSlackMessage = (eventType, data, userEmail) => {
  let emoji, title, color, fields;

  switch (eventType) {
    case 'inventoryAdd':
      emoji = '✨';
      title = '새 재고 추가';
      color = '#36a64f'; // 초록
      fields = [
        { title: '제품명', value: data.productName, short: true },
        { title: 'SKU', value: data.barcodeId || '-', short: true },
        { title: '카테고리', value: data.category, short: true },
        { title: '위치', value: data.location, short: true },
        { title: '구매가', value: `${data.purchasePrice?.toLocaleString()}원` || '-', short: true },
        { title: '판매가', value: `${data.salePrice?.toLocaleString()}원` || '-', short: true }
      ];
      break;

    case 'inventoryUpdate':
      emoji = '🔄';
      title = '재고 정보 수정';
      color = '#3AA3E3'; // 파랑
      fields = [];

      // 변경사항만 필드에 추가
      if (data.changes) {
        Object.keys(data.changes).forEach(key => {
          const change = data.changes[key];
          const fieldName = getFieldName(key);
          fields.push({
            title: fieldName,
            value: `${formatValue(change.old)} → ${formatValue(change.new)}`,
            short: true
          });
        });
      }

      // 기본 정보
      fields.unshift(
        { title: '제품명', value: data.productName, short: true },
        { title: 'SKU', value: data.barcodeId || '-', short: true }
      );
      break;

    case 'inventoryMove':
      emoji = '📦';
      title = '재고 이동';
      color = '#FFA500'; // 주황
      fields = [
        { title: '제품명', value: data.productName, short: true },
        { title: 'SKU', value: data.barcodeId || '-', short: true },
        { title: '이동', value: `${data.fromLocation} → ${data.toLocation}`, short: false }
      ];
      break;

    case 'quantityChange':
      emoji = '🔢';
      title = '수량 변경';
      color = '#FF6B6B'; // 빨강
      fields = [
        { title: '제품명', value: data.productName, short: true },
        { title: 'SKU', value: data.barcodeId || '-', short: true },
        { title: '수량 변경', value: `${data.oldQuantity}개 → ${data.newQuantity}개`, short: true },
        { title: '변경량', value: `${data.newQuantity - data.oldQuantity > 0 ? '+' : ''}${data.newQuantity - data.oldQuantity}개`, short: true }
      ];
      break;

    case 'inventoryDelete':
      emoji = '🗑️';
      title = '재고 삭제';
      color = '#DC143C'; // 빨강
      fields = [
        { title: '제품명', value: data.productName, short: true },
        { title: 'SKU', value: data.barcodeId || '-', short: true },
        { title: '위치', value: data.location, short: true },
        { title: '카테고리', value: data.category, short: true }
      ];
      break;

    case 'sale':
      emoji = '💰';
      title = '판매 완료';
      color = '#28a745'; // 초록
      fields = [
        { title: '제품명', value: data.productName, short: true },
        { title: '판매가', value: `${data.actualSalePrice?.toLocaleString()}원`, short: true },
        { title: '수익', value: `${data.profit?.toLocaleString()}원`, short: true },
        { title: '구매자', value: data.buyerName || '-', short: true }
      ];
      break;

    default:
      emoji = '📌';
      title = '재고 변경';
      color = '#808080';
      fields = [];
  }

  return {
    attachments: [
      {
        color: color,
        pretext: `${emoji} *${title}*`,
        fields: fields,
        footer: `변경자: ${getUserDisplayName(userEmail)}`,
        ts: Math.floor(Date.now() / 1000)
      }
    ]
  };
};

/**
 * 필드명 한글화
 */
const getFieldName = (fieldKey) => {
  const fieldNames = {
    productName: '제품명',
    category: '카테고리',
    location: '위치',
    status: '상태',
    condition: '컨디션',
    purchasePrice: '구매가',
    salePrice: '판매가',
    consignorName: '위탁자',
    consignorCommission: '위탁수수료',
    description: '설명',
    notes: '비고'
  };
  return fieldNames[fieldKey] || fieldKey;
};

/**
 * 값 포맷팅
 */
const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  return String(value);
};
